import { useEffect, useState } from "react";
import { Loader2, Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { formatARS } from "../../lib/site";
import { VehicleDetail } from "./VehicleDetail";

export const KIND = { omnibus: "Ómnibus", tractor: "Camión (tractor)", semirremolque: "Semirremolque", batea: "Batea" };
const nf = (n) => (n == null ? "—" : Number(n).toLocaleString("es-AR"));
const inputCls = "h-10 min-w-0 rounded-xl border border-black/10 bg-white px-3 text-sm";

const NewVehicle = ({ onDone }) => {
  const [f, setF] = useState({ kind: "tractor", interno: "", patente: "", marca: "", modelo: "", anio: "", km: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/fleet/vehicles", { ...f, anio: f.anio ? Number(f.anio) : null, km: Number(f.km || 0) });
      toast.success("Unidad creada"); setF({ ...f, interno: "", patente: "", marca: "", modelo: "", anio: "", km: "" }); onDone();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <form onSubmit={save} data-testid="vehicle-new" className="grid gap-2 rounded-2xl bg-white p-4 ring-1 ring-black/5 sm:grid-cols-4 lg:grid-cols-8">
      <select data-testid="vehicle-new-kind" value={f.kind} onChange={set("kind")} className={inputCls}><option value="tractor">Camión (tractor)</option><option value="semirremolque">Semirremolque</option><option value="batea">Batea</option></select>
      <input data-testid="vehicle-new-interno" required placeholder="Interno" value={f.interno} onChange={set("interno")} className={inputCls} />
      <input data-testid="vehicle-new-patente" required placeholder="Patente" value={f.patente} onChange={set("patente")} className={inputCls} />
      <input placeholder="Marca" value={f.marca} onChange={set("marca")} className={inputCls} />
      <input placeholder="Modelo" value={f.modelo} onChange={set("modelo")} className={inputCls} />
      <input placeholder="Año" inputMode="numeric" value={f.anio} onChange={set("anio")} className={inputCls} />
      <input data-testid="vehicle-new-km" placeholder="Km actuales" inputMode="numeric" value={f.km} onChange={set("km")} className={inputCls} />
      <button data-testid="vehicle-new-save" className="inline-flex h-10 items-center justify-center gap-1 rounded-xl bg-[var(--forest)] px-3 text-sm font-bold text-white"><Plus className="h-4 w-4" /> Agregar</button>
      <p className="text-xs text-[var(--muted-ink)] sm:col-span-4 lg:col-span-8">Los ómnibus se crean en Pasajes → Unidades y aparecen acá solos. Cuota mensual sugerida: tractor US$ 2.500, semirremolque y batea US$ 1.500 (editable en la ficha).</p>
    </form>
  );
};

export const VehiclesPanel = () => {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  const [kind, setKind] = useState("");
  const load = () => api.get("/fleet/vehicles").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch((e) => { toast.error(apiError(e)); setList([]); });
  useEffect(() => { load(); }, []);
  const shown = (list ?? []).filter((v) => !kind || v.kind === kind);
  return (
    <div className="space-y-4">
      <NewVehicle onDone={load} />
      <div className="flex flex-wrap gap-2">{[["", "Todas"], ...Object.entries(KIND)].map(([k, l]) => <button key={k} onClick={() => setKind(k)} data-testid={`fleet-kind-${k || "all"}`} className={`rounded-full px-4 py-2 text-sm font-bold ${kind === k ? "bg-[var(--ink)] text-white" : "bg-white"}`}>{l}</button>)}</div>
      {!list ? <Loader2 className="h-5 w-5 animate-spin" /> : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((v) => (
            <button key={v.id} onClick={() => setOpen(v.id)} data-testid={`vehicle-card-${v.interno}`} className="rounded-2xl bg-white p-4 text-left ring-1 ring-black/5 transition-shadow hover:shadow-lg">
              <div className="flex items-start justify-between gap-2">
                <p className="font-display-up text-xl">Int. {v.interno} <span className="text-sm font-normal text-[var(--muted-ink)]">{v.patente}</span></p>
                <span className="rounded-full bg-[var(--paper)] px-2 py-0.5 text-[11px] font-bold">{KIND[v.kind]}</span>
              </div>
              <p className="mt-1 text-sm text-[var(--muted-ink)]">{[v.marca, v.modelo, v.anio].filter(Boolean).join(" · ") || "—"} · {v.estado ?? "activo"}</p>
              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <p><span className="block text-[var(--muted-ink)]">Km</span><b>{nf(v.km)}</b></p>
                <p><span className="block text-[var(--muted-ink)]">l/100 km</span><b>{v.stats?.l100 ?? "—"}</b></p>
                <p><span className="block text-[var(--muted-ink)]">Costo/km</span><b>{v.stats?.costo_km != null ? formatARS(v.stats.costo_km) : "—"}</b></p>
              </div>
              {v.alerts?.length > 0 && <div className="mt-3 space-y-1">{v.alerts.map((a, i) => <p key={i} className={`flex items-center gap-1 text-xs font-semibold ${a.level === "danger" ? "text-red-700" : "text-amber-700"}`}><AlertTriangle className="h-3.5 w-3.5" /> {a.text}</p>)}</div>}
            </button>
          ))}
          {shown.length === 0 && <p className="text-sm text-[var(--muted-ink)]">No hay unidades.</p>}
        </div>
      )}
      <VehicleDetail id={open} onClose={() => { setOpen(null); load(); }} />
    </div>
  );
};
